/**
 * WebRTC Realtime Hook
 * CLIENT-SIDE direct connection to OpenAI Realtime API
 * 
 * This replaces the Socket.IO approach for better performance:
 * - Lower latency (~200-300ms vs ~500-800ms)
 * - Better scalability (OpenAI handles load)
 * - Lower server costs (no audio processing)
 * - Native WebRTC on mobile
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useHaptics } from '../utils/haptics';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';

export function useWebRTCRealtime() {
  const { showError, showSuccess } = useNotifications();
  const { user } = useAuth();
  const haptics = useHaptics();
  
  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [userTranscript, setUserTranscript] = useState('');
  const [aiTranscript, setAiTranscript] = useState('');
  
  // ✅ PHASE 1: Initialize threadId from localStorage on mount
  const [threadId, setThreadId] = useState(() => {
    try {
      const saved = localStorage.getItem('ai_last_thread');
      if (saved && saved !== 'null' && saved !== 'undefined') {
        console.log(`💾 Restored threadId from localStorage: ${saved.substring(0, 8)}...`);
        return saved;
      }
    } catch (error) {
      console.warn('⚠️ Failed to restore threadId from localStorage:', error);
    }
    return null;
  })
  
  // Refs
  const peerConnectionRef = useRef(null);
  const connectingRef = useRef(false); // ✅ Guards against opening a second concurrent AI session
  const dataChannelRef = useRef(null);
  const sessionIdRef = useRef(null);
  const conversationIdRef = useRef(null); // ✅ FIX: Store conversation ID for memory persistence
  const threadIdRef = useRef(null);       // ✅ PHASE 1: Store threadId in ref too
  const addMessageRef = useRef(null);
  const playbackAudioContextRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const nextPlayTimeRef = useRef(0);
  const currentSourcesRef = useRef([]);
  const isAIActiveRef = useRef(false);
  const idleTimerRef = useRef(null);  // ✅ PHASE 4: Idle timeout timer
  
  // ✅ PHASE 1: Persist threadId to localStorage whenever it changes
  useEffect(() => {
    if (threadId) {
      try {
        localStorage.setItem('ai_last_thread', threadId);
        threadIdRef.current = threadId;
        console.log(`💾 Saved threadId to localStorage: ${threadId.substring(0, 8)}...`);
      } catch (error) {
        console.warn('⚠️ Failed to save threadId to localStorage:', error);
      }
    }
  }, [threadId]);
  
  /**
   * ✅ PHASE 4: Reset idle timer on user activity
   */
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    
    // Set 15-minute idle timeout (ChatGPT-style)
    idleTimerRef.current = setTimeout(() => {
      if (isConnected && peerConnectionRef.current) { 
        console.log('⏰ Session idle for 15 minutes - disconnecting...');
        if (addMessageRef.current) {
          addMessageRef.current({
            sender: 'system',
            text: 'Session ended due to inactivity.',
            type: 'system'
          });
        }
        // Close connection directly
        peerConnectionRef.current.close();
        setIsConnected(false);
        setIsListening(false);
      }
    }, 15 * 60 * 1000); // 15 minutes
  }, [isConnected]); // Removed disconnect from deps
  
  /**
   * ✅ PHASE 4: Graceful shutdown on page unload
   */
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isConnected && peerConnectionRef.current) {
        console.log('👋 Page unloading - closing connection gracefully...');
        // Close connection directly without async
        peerConnectionRef.current.close();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [isConnected]); // Removed disconnect and logEvent from deps
  
  /**
   * Get ephemeral token from backend
   */
  const getEphemeralToken = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // ✅ Send threadId: null for new conversation, or actual threadId to continue
      const body = {
        threadId: threadIdRef.current || null  // null = create new, uuid = continue existing
      };
      
      const response = await fetch(`${API_BASE_URL}/api/realtime/token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Token generation failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Token generation failed');
      }

      console.log('✅ Ephemeral token received');
      return data;

    } catch (error) {
      console.error('❌ Error getting ephemeral token:', error);
      throw error;
    }
  };

  /**
   * Log event to backend
   */
  const logEvent = useCallback(async (eventType, data) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/realtime/log`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          threadId: threadIdRef.current, // ✅ Links voice transcripts to the conversation so text chat sees them
          eventType,
          data,
          timestamp: Date.now()
        })
      });
    } catch (error) {
      console.error('⚠️ Failed to log event:', error);
      // Don't throw - logging failures shouldn't break the app
    }
  }, []);

  /**
   * Execute function call via backend
   */
  const executeFunctionViaBackend = async (functionName, args, callId) => {
    try {
      console.log(`⚙️ Executing ${functionName} via backend...`);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/realtime/execute-function`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          functionName,
          args: typeof args === 'string' ? JSON.parse(args) : args,
          callId
        })
      });
      
      const result = await response.json();
      
      console.log(`✅ Function ${functionName} executed:`, result);
      
      // Send result back to OpenAI via WebRTC data channel
      if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
        dataChannelRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify(result.data || result)
          }
        }));
        
        // Tell OpenAI to generate response based on result
        dataChannelRef.current.send(JSON.stringify({
          type: 'response.create'
        }));
        
        console.log('📤 Function result sent back to OpenAI');
      }
      
    } catch (error) {
      console.error(`❌ Error executing ${functionName}:`, error);
      showError(`Failed to execute ${functionName}`);
    }
  };

  /**
   * Play next audio chunk from queue
   */
  const playNextAudioChunk = async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }

    isPlayingRef.current = true;
    const audioData = audioQueueRef.current.shift();

    try {
      // Create playback AudioContext if it doesn't exist
      if (!playbackAudioContextRef.current) {
        playbackAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
        nextPlayTimeRef.current = playbackAudioContextRef.current.currentTime;
      }
      
      // Resume if suspended
      if (playbackAudioContextRef.current.state === 'suspended') {
        await playbackAudioContextRef.current.resume();
      }
      
      // Decode base64 PCM16 audio
      const binaryString = atob(audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Convert PCM16 to Float32
      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768.0;
      }
      
      // Create audio buffer
      const audioBuffer = playbackAudioContextRef.current.createBuffer(1, float32.length, 24000);
      audioBuffer.getChannelData(0).set(float32);
      
      // Create source and play
      const source = playbackAudioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.playbackRate.value = 1.1; // 10% faster
      source.connect(playbackAudioContextRef.current.destination);
      
      // Track this source
      currentSourcesRef.current.push(source);
      
      // Remove from tracking when finished
      source.onended = () => {
        const index = currentSourcesRef.current.indexOf(source);
        if (index > -1) {
          currentSourcesRef.current.splice(index, 1);
        }
      };
      
      // Schedule playback
      const currentTime = playbackAudioContextRef.current.currentTime;
      const playTime = Math.max(currentTime, nextPlayTimeRef.current);
      source.start(playTime);
      
      // Update next play time
      const duration = audioBuffer.duration / source.playbackRate.value;
      nextPlayTimeRef.current = playTime + duration;
      
      // Continue processing queue
      if (audioQueueRef.current.length > 0) {
        playNextAudioChunk();
      } else {
        isPlayingRef.current = false;
      }
      
    } catch (error) {
      console.error('❌ Error playing audio:', error);
      playNextAudioChunk(); // Continue to next chunk
    }
  };

  /**
   * Handle OpenAI server events
   */
  const handleServerEvent = useCallback((event) => {
    console.log('📨 OpenAI event:', event.type);

    switch (event.type) {
      case 'session.created':
        console.log('✅ Session created:', event.session.id);
        sessionIdRef.current = event.session.id;
        logEvent('session_start', { session_id: event.session.id });
        break;

      case 'session.updated':
        console.log('✅ Session updated');
        break;

      case 'input_audio_buffer.speech_started':
        console.log('🛑 USER INTERRUPTION - Stopping AI');
        
        // Stop all playing audio
        currentSourcesRef.current.forEach(source => {
          try {
            source.stop();
          } catch (e) {
            // Already stopped
          }
        });
        currentSourcesRef.current = [];
        
        // Clear audio queue
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        
        // Reset playback timer
        if (playbackAudioContextRef.current) {
          nextPlayTimeRef.current = playbackAudioContextRef.current.currentTime;
        }
        break;

      case 'input_audio_buffer.speech_stopped':
        console.log('✅ User stopped speaking');
        break;

      case 'response.created':
        console.log('🤖 AI response started');
        isAIActiveRef.current = true;
        if (playbackAudioContextRef.current) {
          nextPlayTimeRef.current = playbackAudioContextRef.current.currentTime;
        }
        break;

      case 'response.done':
        console.log('✅ AI response complete');
        isAIActiveRef.current = false;
        break;

      case 'response.cancelled':
        console.log('🚫 AI response cancelled');
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        break;

      case 'response.output_audio_transcript.delta':
        if (event.delta) {
          setAiTranscript(prev => prev + event.delta);
        }
        break;

      case 'response.output_audio_transcript.done':
        console.log('💬 AI transcript ready:', event.transcript);
        if (event.transcript && addMessageRef.current) {
          addMessageRef.current({
            sender: 'ai',
            text: event.transcript,
            type: 'voice'
          });
          
          // Log to backend
          logEvent('ai_response', {
            transcript: event.transcript,
            duration: event.audio_duration_ms,
            response_id: event.response_id
          });
        }
        setAiTranscript('');
        break;

      // ✅ PHASE 3: Handle text responses from Realtime API
      case 'response.text.delta':
        if (event.delta) {
          setAiTranscript(prev => prev + event.delta);
        }
        break;

      case 'response.text.done':
        console.log('💬 [WebRTC Text] AI text response ready:', event.text);
        if (event.text && addMessageRef.current) {
          addMessageRef.current({
            sender: 'ai',
            text: event.text,
            type: 'text'
          });
          
          // Log to backend
          logEvent('ai_text_response', {
            text: event.text,
            response_id: event.response_id
          });
        }
        setAiTranscript('');
        break;

      case 'conversation.item.input_audio_transcription.completed':
        console.log('📝 User transcript ready:', event.transcript);
        if (event.transcript && addMessageRef.current) {
          addMessageRef.current({
            sender: 'user',
            text: event.transcript,
            type: 'voice'
          });
          
          // Log to backend
          logEvent('user_speech', {
            transcript: event.transcript,
            duration: event.audio_duration_ms,
            item_id: event.item_id
          });
        }
        setUserTranscript('');
        break;

      case 'response.function_call_arguments.done':
        // Function call detected - execute via backend
        console.log('🔧 Function call detected:', event.name, event.arguments);
        executeFunctionViaBackend(event.name, event.arguments, event.call_id);
        break;

      case 'response.output_audio.delta':
        if (event.delta) {
          audioQueueRef.current.push(event.delta);
          if (!isPlayingRef.current) {
            playNextAudioChunk();
          }
        }
        break;

      case 'error':
        console.error('❌ OpenAI error:', event.error);
        showError('AI conversation error: ' + event.error.message);
        break;

      default:
        // Log other events for debugging
        if (!['rate_limits.updated'].includes(event.type)) {
          console.log('📝 Event:', event.type);
        }
    }
  }, [logEvent, showError]);

  /**
   * Initialize WebRTC connection
   * ✅ PHASE 1 FIX: Accept existingThreadId parameter to continue conversations
   */
  const connect = async (addMessage, existingThreadId = null) => {
    // ✅ One live session at a time: a second connect used to open a second AI voice
    if (connectingRef.current) {
      console.warn('⚠️ Connect already in progress, ignoring duplicate start');
      return;
    }
    connectingRef.current = true;

    try {
      if (peerConnectionRef.current) {
        console.log('⚠️ Existing WebRTC session open, closing it before reconnecting');
        await disconnect(addMessage);
      }

      setIsConnecting(true);
      addMessageRef.current = addMessage;
      haptics.light();

      console.log('🔌 Connecting to OpenAI Realtime API via WebRTC...');
      
      // ✅ PHASE 1 FIX: Set threadId BEFORE getting token if we're continuing a conversation
      if (existingThreadId) {
        threadIdRef.current = existingThreadId;
        console.log(`💬 Using existing threadId: ${existingThreadId.substring(0, 8)}...`);
      }

      // 1. Get ephemeral token from backend
      const tokenData = await getEphemeralToken();
      const ephemeralKey = tokenData.ephemeralKey;
      
      // ✅ PHASE 1: Store threadId for memory persistence
      if (tokenData.threadId) {
        setThreadId(tokenData.threadId);
        threadIdRef.current = tokenData.threadId;
        conversationIdRef.current = tokenData.threadId; // Backward compatibility
        console.log(`💬 Using conversation thread: ${tokenData.threadId.substring(0, 8)}...`);
      } else if (tokenData.conversationId) {
        // Fallback to old conversationId if threadId not available
        setThreadId(tokenData.conversationId);
        conversationIdRef.current = tokenData.conversationId;
        threadIdRef.current = tokenData.conversationId;
        console.log(`💬 Using conversation ID: ${tokenData.conversationId.substring(0, 8)}...`);
      }

      // 2. Create peer connection
      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      // ✅ PHASE 4: Monitor ICE connection state for network drops
      pc.oniceconnectionstatechange = () => {
        console.log(`🌐 ICE Connection State: ${pc.iceConnectionState}`);
        
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          console.warn('⚠️  Connection lost - attempting to reconnect...');
          
          // Auto-reconnect to same thread
          if (threadIdRef.current && addMessageRef.current) {
            addMessageRef.current({
              sender: 'system',
              text: 'Connection lost. Reconnecting...',
              type: 'system'
            });
            
            // Disconnect gracefully
            disconnect();
            
            // Reconnect after a short delay
            setTimeout(() => {
              console.log('🔄 Auto-reconnecting to same thread...');
              connect(addMessageRef.current);
            }, 2000);
          }
        } else if (pc.iceConnectionState === 'connected') {
          console.log('✅ ICE Connection established');
        }
      };

      // ✅ PHASE 4: Monitor general connection state
      pc.onconnectionstatechange = () => {
        console.log(`🔌 Connection State: ${pc.connectionState}`);
      };

      // 3. Set up to play remote audio from the model
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      pc.ontrack = (e) => {
        console.log('🔊 Received remote audio track');
        audioEl.srcObject = e.streams[0];
      };

      // 4. Add local audio track for microphone input
      const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
      ms.getTracks().forEach(track => pc.addTrack(track, ms));
      console.log('🎤 Added local audio track');

      // 5. Set up data channel for events
      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;

      dc.onopen = () => {
        console.log('✅ Data channel opened');
        setIsConnected(true);
        setIsListening(true);
        showSuccess('AI conversation ready!');
        
        if (addMessageRef.current) {
          addMessageRef.current({
            sender: 'system',
            text: 'AI conversation started via WebRTC. Speak naturally!',
            type: 'system'
          });
        }
      };

      dc.onclose = () => {
        console.log('🔌 Data channel closed');
        setIsConnected(false);
        setIsListening(false);
      };

      dc.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          handleServerEvent(event);
        } catch (error) {
          console.error('❌ Error parsing server event:', error);
        }
      };

      // 6. Create offer and connect to OpenAI
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      console.log('📤 Sending SDP offer to OpenAI...');

      const sdpResponse = await fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp'
        },
        body: offer.sdp
      });

      if (!sdpResponse.ok) {
        throw new Error(`SDP exchange failed: ${sdpResponse.status}`);
      }

      const answerSdp = await sdpResponse.text();
      const answer = {
        type: 'answer',
        sdp: answerSdp
      };

      await pc.setRemoteDescription(answer);
      console.log('✅ WebRTC connection established!');
      
      // ✅ PHASE 2: Use sessionId as call_id (OpenAI doesn't put call_id in SDP)
      // The session ID from OpenAI is the call_id we need
      if (sessionIdRef.current) {
        try {
          console.log(`🔑 Using session ID as call_id: ${sessionIdRef.current.substring(0, 8)}...`);
          await axios.post(`${API_BASE_URL}/api/realtime/sideband`, {
            callId: sessionIdRef.current, // Use session ID as call_id
            threadId: threadIdRef.current,
            sessionId: sessionIdRef.current
          });
          console.log('✅ Sideband connection initiated');
        } catch (sidebandError) {
          console.warn('⚠️  Failed to initiate sideband connection:', sidebandError);
          // Don't fail the connection if sideband setup fails
        }
      } else {
        console.warn('⚠️  No session ID available for sideband connection');
      }

    } catch (error) {
      console.error('❌ Error connecting via WebRTC:', error);
      showError('Failed to start AI conversation: ' + error.message);
      setIsConnected(false);
      setIsListening(false);
      throw error;
    } finally {
      setIsConnecting(false);
      connectingRef.current = false;
    }
  };

  /**
   * Disconnect WebRTC
   */
  const disconnect = async (addMessage) => {
    try {
      console.log('🛑 Disconnecting WebRTC...');

      setIsListening(false);
      setIsConnected(false);

      // Log session end
      if (sessionIdRef.current) {
        await logEvent('session_end', {});
      }

      // ✅ CRITICAL FIX: Stop all microphone audio tracks FIRST
      if (peerConnectionRef.current) {
        const senders = peerConnectionRef.current.getSenders();
        senders.forEach(sender => {
          if (sender.track) {
            sender.track.stop();
            console.log('🎤 Stopped audio track:', sender.track.kind, sender.track.label);
          }
        });
      }

      // Close data channel
      if (dataChannelRef.current) {
        dataChannelRef.current.close();
        dataChannelRef.current = null;
      }

      // Close peer connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      // Stop audio playback
      currentSourcesRef.current.forEach(source => {
        try {
          source.stop();
        } catch (e) {
          // Already stopped
        }
      });
      currentSourcesRef.current = [];

      if (playbackAudioContextRef.current) {
        playbackAudioContextRef.current.close();
        playbackAudioContextRef.current = null;
      }

      audioQueueRef.current = [];
      isPlayingRef.current = false;
      nextPlayTimeRef.current = 0;
      sessionIdRef.current = null;

      if (addMessage) {
        addMessage({
          sender: 'system',
          text: 'AI conversation ended.',
          type: 'system'
        });
      }

      console.log('✅ WebRTC disconnected');

    } catch (error) {
      console.error('❌ Error disconnecting:', error);
    }
  };

  /**
   * Send event to OpenAI
   */
  const sendEvent = useCallback((event) => {
    if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
      dataChannelRef.current.send(JSON.stringify(event));
    } else {
      console.warn('⚠️ Data channel not open, cannot send event');
    }
  }, []);

  /**
   * ✅ PHASE 3: Send text message via WebRTC data channel
   * This enables text mode to use Realtime API instead of Chat Completions
   */
  const sendTextMessage = useCallback(async (text) => {
    if (!text || !text.trim()) {
      console.warn('⚠️ Cannot send empty message');
      return;
    }

    if (!isConnected) {
      console.warn('⚠️ Not connected to Realtime API');
      return;
    }

    // ✅ PHASE 4: Reset idle timer on user activity
    resetIdleTimer();

    try {
      console.log(`📤 [WebRTC Text] Sending message: "${text.substring(0, 50)}..."`);

      // Create conversation item with text content
      const event = {
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{
            type: 'input_text',
            text: text
          }]
        }
      };

      // Send to OpenAI
      sendEvent(event);

      // Trigger response generation
      const responseEvent = {
        type: 'response.create'
      };
      sendEvent(responseEvent);

      console.log('✅ [WebRTC Text] Message sent successfully');

      // Log to backend
      await logEvent('text.message_sent', {
        text: text,
        length: text.length,
        threadId: threadIdRef.current
      });

    } catch (error) {
      console.error('❌ Error sending text message:', error);
      throw error;
    }
  }, [isConnected, sendEvent, logEvent, resetIdleTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isConnected) {
        disconnect();
      }
    };
  }, [isConnected]);

  /**
   * Toggle microphone mute/unmute
   */
  const toggleMute = useCallback(() => {
    if (!peerConnectionRef.current) {
      console.warn('⚠️ No peer connection to toggle mute');
      return;
    }

    const senders = peerConnectionRef.current.getSenders();
    const audioSender = senders.find(sender => sender.track && sender.track.kind === 'audio');

    if (audioSender && audioSender.track) {
      const newMutedState = !audioSender.track.enabled;
      audioSender.track.enabled = !newMutedState;
      setIsListening(!newMutedState);
      
      console.log(`🎤 Microphone ${newMutedState ? 'MUTED' : 'UNMUTED'}`);
      return !newMutedState; // Return new listening state
    }

    console.warn('⚠️ No audio track found to toggle');
    return isListening;
  }, [isListening]);

  /**
   * ✅ PHASE 1: Clear threadId and start fresh conversation
   */
  const clearThread = useCallback(() => {
    try {
      localStorage.removeItem('ai_last_thread');
      setThreadId(null);
      threadIdRef.current = null;
      conversationIdRef.current = null;
      console.log('🧹 Cleared threadId from state and localStorage');
    } catch (error) {
      console.warn('⚠️ Failed to clear threadId from localStorage:', error);
    }
  }, []);

  return {
    isConnected,
    isConnecting,
    isListening,
    userTranscript,
    aiTranscript,
    threadId,                        // ✅ PHASE 1: Expose threadId
    conversationId: threadId,        // Alias for backward compatibility
    connect,
    disconnect,
    sendEvent,
    sendTextMessage,                 // ✅ PHASE 3: Expose text message sending
    toggleMute,
    clearThread                      // ✅ PHASE 1: Expose clearThread method
  };
}

